import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpApiService } from '@core/http-legacy';
import type { PaginationMeta } from '@core/http-types';
import type { PipelinePlacement, PipelineStage } from '@core/placements-types';
import type {
  PipelineParams,
  PipelineParamsWithStage,
  PaginatedPipelineParams,
} from '@core/stages-types';
import { getOptions } from '@core/walkthrough-util';

import { defaultPipelineParams } from './consts';

@Injectable({ providedIn: 'root' })
export class PipelineStagesHttpService {
  private readonly httpApi = inject(HttpApiService);

  public fetchOne$(
    id: string,
    params: PipelineParams = defaultPipelineParams,
  ): Observable<{ stages: PipelineStage[]; id: string }> {
    return this.httpApi
      .get(`/offers/${id}/placements`, params, getOptions({ hasOnboarding: true }))
      .pipe(
        map(response => ({
          stages: response.stages,
          id,
        })),
      );
  }

  public fetchStagePlacements$(
    id: string,
    params: PipelineParamsWithStage,
  ): Observable<PipelinePlacement[]> {
    return this.httpApi
      .get(`/offers/${id}/placements`, params)
      .pipe(map(response => response.placements));
  }

  public fetchStagePlacementsPaginated$(
    offerSlug: string,
    params: PaginatedPipelineParams,
  ): Observable<{ placements: PipelinePlacement[]; meta: PaginationMeta }> {
    return this.httpApi
      .get(`/offers/${offerSlug}/stages/${params.stage_id}/placements`, params)
      .pipe(
        map(response => ({
          placements: response.placements,
          meta: {
            page: response.meta.page,
            limit: response.meta.limit,
            totalCount: response.meta.totalCount,
          },
        })),
      );
  }

  public fetchPlacement(
    offerId: number,
    candidateId: number,
    params: PipelineParams = defaultPipelineParams,
  ): Observable<PipelinePlacement> {
    return this.httpApi
      .get(`/offers/${offerId}/candidates/${candidateId}/placement`, params)
      .pipe(map(response => response.placement));
  }
}
